import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getErrorDetail } from '@/utils/apiError';
import { cn } from '@/utils/cn';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Tabs } from '@/components/UI/Tabs';
import { Alert } from '@/components/UI/Alert';
import { Button, buttonVariants } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import {
  useGetAgentDraftQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  usePublishAgentVersionMutation,
  useListToolsQuery,
  useListScreensQuery,
  useGetParameterCatalogueQuery,
} from '@/redux/internal-services/agentsApi';

import {
  AgentFormValues,
  agentSchema,
  emptyAgent,
  AgentTypeBadge,
  KNOWN_MODULES,
  UNREACHABLE_MODULES,
  splitDraftMeta,
} from '@/components/Agent/agentShared';
import OverviewTab from '@/components/Agent/OverviewTab';
import InstructionsTab from '@/components/Agent/InstructionsTab';
import ToolsTab from '@/components/Agent/ToolsTab';
import ScreensTab from '@/components/Agent/ScreensTab';
import CardsTab from '@/components/Agent/CardsTab';
import PromptOrderTab from '@/components/Agent/PromptOrderTab';
import { PromptChecksContext, usePromptChecksModel } from '@/components/Agent/usePromptChecks';
import { LayoutDraftsContext, useLayoutDraftsModel } from '@/components/Agent/layoutDrafts';
import WorkflowTab from '@/components/Agent/WorkflowTab';
import { VersionsTab, SaveVersionModal } from '@/components/Agent/VersionsTab';
import PromptTab from '@/components/Agent/PromptTab';
import TestPanel from '@/components/Agent/TestPanel';

// ── types ──────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'instructions' | 'order' | 'tools' | 'screens' | 'cards' | 'advanced' | 'workflow' | 'test' | 'prompt' | 'versions';
type ModalState =
  | null
  | { kind: 'saveVersion' };

const isConflict = (err: unknown) => (err as { status?: number })?.status === 409;

// ── AgentConfigPage ────────────────────────────────────────────────────────────

const AgentConfigPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = Boolean(agentId && agentId !== 'new');
  const navigate = useNavigate();

  // The editor works on the DRAFT (latest saved version), which can be ahead of the live agent until it is published.
  const { data: existing, refetch: reloadDraft } = useGetAgentDraftQuery(agentId as string, { skip: !isEdit });
  const { data: allTools } = useListToolsQuery();
  const { data: screens } = useListScreensQuery();
  const { data: paramCat } = useGetParameterCatalogueQuery();
  const [createAgent, { isLoading: creating }] = useCreateAgentMutation();
  const [updateAgent, { isLoading: updating }] = useUpdateAgentMutation();
  const [publishVersion] = usePublishAgentVersionMutation();

  const [tab, setTab] = useState<Tab>('overview');
  const [modal, setModal] = useState<ModalState>(null);
  const [savedFlash, setSavedFlash] = useState('');
  const [serverError, setServerError] = useState('');
  // Someone else saved a newer version while this form was open (409): offer to reload instead of overwriting.
  const [conflict, setConflict] = useState(false);
  const [saveError, setSaveError] = useState('');
  // The validated form values waiting for the Save dialog's comment.
  const pendingValues = useRef<AgentFormValues | null>(null);

  // ── form ────────────────────────────────────────────────────────────────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const methods = useForm<AgentFormValues>({
    resolver: zodResolver(agentSchema) as any,
    defaultValues: emptyAgent,
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isDirty, errors },
  } = methods;

  // Template check (syntax / unknown variables) for every prompt block: run from the Instructions tab button and before saving.
  const promptChecks = usePromptChecksModel(watch('system_prompts') ?? []);
  // Reply-layout editors on the Tools tab that hold a layout with problems (see the check in onSubmit).
  const layoutDrafts = useLayoutDraftsModel();

  // Load the form from a draft. The history bookkeeping the server adds (version numbers, publish state) stays out of the
  // form so it can never be sent back as configuration.
  const loadDraft = (draft: Record<string, unknown>) => {
    const { config } = splitDraftMeta(draft);
    reset({ ...emptyAgent, ...config } as AgentFormValues);
  };

  // Populate form when server data loads
  useEffect(() => {
    if (existing) loadDraft(existing as unknown as Record<string, unknown>);
  }, [existing, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  // A NEW agent starts with the cross-cutting core tools (knowledge-base search, screen context, calendar,
  // notifications) already selected. Seeded once: if the admin removes them in the Tools tab they stay
  // removed, because the ref stops this re-adding them when the catalogue refetches. The backend never
  // injects tools an agent does not own, so what is selected here is exactly what the agent gets.
  const coreSeeded = useRef(false);
  useEffect(() => {
    if (isEdit || coreSeeded.current || !allTools || !screens) return;
    coreSeeded.current = true;
    const core = new Set(screens.core_tools);
    const coreRefs = allTools
      .filter((t) => t.name && core.has(t.name))
      .map((t) => ({
        tool_id: t.tool_id,
        name: t.name,
        tool_name: t.tool_name || t.name,
        description: t.description,
        requires_confirmation: t.requires_confirmation ?? false,
        argument_schema: t.argument_schema,
        toolkit_type: t.toolkit_type,
        toolkit_name: t.toolkit_name,
        action_card_template: t.action_card_template,
      }));
    if (coreRefs.length && !(getValues('tools') || []).length) {
      setValue('tools', coreRefs, { shouldDirty: false });
    }
  }, [allTools, screens, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── derived state ────────────────────────────────────────────────────────────

  const agentType = watch('agent_type');
  const isWorkflow = agentType === 'workflow';

  // Where the draft stands: HEAD is the latest saved version, LIVE is the one published (what the runtime uses).
  const headVersion = existing?.head_version;
  const liveVersion = existing?.published_version ?? null;

  const paramGroups = paramCat?.groups || {};

  const tabs: Array<{ k: Tab; l: string }> = [
    { k: 'overview', l: 'Overview' },
    { k: 'instructions', l: 'Instructions' },
    { k: 'order', l: 'Prompt order' },
    { k: 'tools', l: 'Tools' },
    { k: 'screens', l: 'Modules' },
    { k: 'cards', l: 'Cards' },
    { k: 'prompt', l: 'Prompt preview' },
    { k: 'advanced', l: 'Advanced' },
    ...(isEdit && isWorkflow ? [{ k: 'workflow' as Tab, l: 'Workflow' }] : []),
    ...(isEdit ? [{ k: 'test' as Tab, l: 'Test' }] : []),
    ...(isEdit ? [{ k: 'versions' as Tab, l: 'History' }] : []),
  ];

  // ── actions ──────────────────────────────────────────────────────────────────

  const flash = (message: string) => {
    setSavedFlash(message);
    setTimeout(() => setSavedFlash(''), 2600);
  };

  /** Save. An existing agent gets a new VERSION (the server decides whether anything changed); it goes live only if
   *  `publish` is set or when it is published later from History. A new agent is created as version 1. */
  const persist = async (values: AgentFormValues, opts: { comment?: string; publish?: boolean } = {}): Promise<boolean> => {
    setServerError('');
    setSaveError('');
    setConflict(false);
    // Stamp order from array index so backend sort matches the UI order.
    const payload = {
      ...values,
      system_prompts: (values.system_prompts || []).map((b, i) => ({ ...b, order: i })),
    };
    try {
      if (isEdit) {
        let res = await updateAgent({
          id: agentId as string,
          body: { ...payload, change_comment: opts.comment || undefined, base_version: headVersion },
        }).unwrap();
        const created = res.version_created !== false;
        if (opts.publish && res.head_version != null && res.published_version !== res.head_version) {
          res = await publishVersion({ id: agentId as string, version: res.head_version }).unwrap();
        }
        // Show what the server now holds, so "unsaved" clears even when only defaults differed.
        loadDraft(res as unknown as Record<string, unknown>);
        flash(
          !created
            ? 'No changes: nothing to save'
            : opts.publish
              ? `✓ Saved as v${res.head_version} and live`
              : `✓ Saved as v${res.head_version}`,
        );
      } else {
        await createAgent(payload).unwrap();
        flash('✓ Saved');
      }
      return true;
    } catch (err: unknown) {
      const message = getErrorDetail(err) ?? 'Failed to save agent.';
      if (isConflict(err)) {
        setConflict(true);
        setModal(null);
        setServerError(message);
      } else if (modal?.kind === 'saveVersion') {
        setSaveError(message);
      } else {
        setServerError(message);
      }
      return false;
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    // Check the templates first: a mistake would otherwise reach the model verbatim (the runtime never fails on one).
    try {
      const { errorCount, blocksWithErrors } = await promptChecks.checkNow();
      if (errorCount > 0) {
        setTab('instructions');
        const go = window.confirm(
          `${errorCount} template problem${errorCount === 1 ? '' : 's'} in ${blocksWithErrors} prompt block${blocksWithErrors === 1 ? '' : 's'} (shown in red on the Instructions tab).

Save anyway?`,
        );
        if (!go) return;
      }
    } catch { /* the check is advisory: if it cannot run, saving still works */ }
    // A reply-layout editor with problems never reached the form: saving would keep its last valid layout.
    const badLayouts = layoutDrafts.invalidCount();
    if (badLayouts > 0) {
      setTab('tools');
      const go = window.confirm(
        `${badLayouts} reply layout${badLayouts === 1 ? ' has' : 's have'} problems (shown in red on the Tools tab), so ${badLayouts === 1 ? 'its' : 'their'} last valid version would be saved.

Save anyway?`,
      );
      if (!go) return;
    }
    if (!isEdit) {
      const ok = await persist(values as AgentFormValues);
      if (ok) navigate('/platform/agents');
      return;
    }
    if (!isDirty) {
      flash('No changes: nothing to save');
      return;
    }
    // Saving an existing agent records a version: ask what changed first.
    pendingValues.current = values as AgentFormValues;
    setSaveError('');
    setModal({ kind: 'saveVersion' });
  });

  const confirmSave = async (comment: string, publish: boolean) => {
    if (!pendingValues.current) return;
    const ok = await persist(pendingValues.current, { comment, publish });
    if (ok) {
      pendingValues.current = null;
      setModal(null);
    }
  };

  const reloadLatest = async () => {
    setConflict(false);
    setServerError('');
    const res = await reloadDraft();
    if (res.data) loadDraft(res.data as unknown as Record<string, unknown>);
  };

  // Every module an admin could preview as the active screen — same union ScreensTab.tsx computes
  // (known modules, minus unreachable ones, plus any extra key already in tool_scope_overrides.screens).
  const previewModules = useMemo(() => {
    const extra = Object.keys(watch('tool_scope_overrides')?.screens ?? {}).filter((m) => !KNOWN_MODULES.includes(m) && !m.includes('.') && !m.includes('#'));
    return [...KNOWN_MODULES, ...extra].filter((m) => !UNREACHABLE_MODULES.has(m));
  }, [watch('tool_scope_overrides')]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportAgent = () => {
    const values = getValues();
    const blob = new Blob([JSON.stringify(values, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(values.agent_name || 'agent').replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importAgent = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const imported = JSON.parse(await file.text());
      // An exported file from before the server owned history may carry a `versions` array: history is not importable.
      const { config } = splitDraftMeta(imported);
      // keepDefaultValues: the imported values count as edits (unsaved), so Save Changes records them as a version.
      reset({ ...emptyAgent, ...config } as AgentFormValues, { keepDefaultValues: true });
      setServerError('');
    } catch {
      setServerError('Import failed — file is not valid agent JSON.');
    }
    e.target.value = '';
  };

  // ── render ───────────────────────────────────────────────────────────────────

  const agentName = watch('agent_name');
  const nextVersionN = (headVersion ?? 0) + 1;

  return (
    <FormProvider {...methods}>
      <PromptChecksContext.Provider value={promptChecks}>
      <LayoutDraftsContext.Provider value={layoutDrafts}>
      <form onSubmit={onSubmit} noValidate className="h-full">
        <PageShell
          maxWidth="max-w-4xl"
          header={
            <PageHeader
              back={
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  onClick={() => navigate('/platform/agents')}
                >
                  ← Agents
                </Button>
              }
              title={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate">{isEdit ? (agentName || 'Edit Agent') : 'New Agent'}</span>
                  <AgentTypeBadge type={agentType} />
                </span>
              }
              subtitle={
                <>
                  {isEdit && liveVersion != null ? (
                    <span className="text-success-text">v{liveVersion} live</span>
                  ) : isEdit ? (
                    'not published'
                  ) : null}
                  {isEdit && headVersion != null && headVersion !== liveVersion ? (
                    <span className="text-warning-text"> · v{headVersion} saved, not live</span>
                  ) : null}
                  {isEdit && isDirty ? (
                    <span className="text-warning-text"> · unsaved edits</span>
                  ) : null}
                  {isEdit && agentId && (
                    <span
                      className="ml-1 cursor-copy font-mono text-ui-text3 hover:text-ui-text2"
                      title="Click to copy agent ID"
                      onClick={() => navigator.clipboard.writeText(agentId)}
                    > · {agentId}</span>
                  )}
                </>
              }
              actions={
                <>
                  {savedFlash && <span className="text-xs font-medium text-success-text" role="status">{savedFlash}</span>}
                  {isDirty && !savedFlash && (
                    <span className="text-xs font-medium text-warning-text">Unsaved changes</span>
                  )}

                  <Button type="button" variant="outline" size="sm" onClick={exportAgent}>
                    Export
                  </Button>
                  <label className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'cursor-pointer')}>
                    Import
                    <input type="file" accept="application/json" className="hidden" onChange={importAgent} />
                  </label>

                  <Button type="submit" size="sm" disabled={creating || updating}>
                    {creating || updating ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Agent'}
                  </Button>
                </>
              }
              toolbar={
                <Tabs
                  label="Agent configuration"
                  tabs={tabs.map((t) => ({ id: t.k, label: t.l }))}
                  value={tab}
                  onChange={(id) => setTab(id as Tab)}
                />
              }
            />
          }
        >

          {/* Validation / server errors */}
          {(serverError || errors.agent_name || errors.models?.text) && (
            <Alert tone="danger" className="mb-4 px-4 py-3 text-sm">
              {serverError ||
                errors.agent_name?.message ||
                errors.models?.text?.message}
              {conflict && (
                <Button type="button" variant="outline" size="xs" className="ml-3" onClick={reloadLatest}>
                  Load the latest version (discards your edits)
                </Button>
              )}
            </Alert>
          )}

          {tab === 'overview' && (
            <OverviewTab
              isEdit={isEdit}
              paramGroups={paramGroups}
            />
          )}

          {tab === 'instructions' && (
            <InstructionsTab />
          )}

          {tab === 'tools' && <ToolsTab allTools={allTools || []} />}

          {tab === 'order' && <PromptOrderTab onEdit={(kind) => setTab(kind === 'global' ? 'instructions' : kind === 'card' ? 'cards' : 'screens')} />}
          {tab === 'screens' && <ScreensTab />}
          {tab === 'cards' && <CardsTab />}

          {tab === 'advanced' && (
            <AdvancedTab paramGroups={paramGroups} />
          )}

          {tab === 'workflow' && isEdit && isWorkflow && (
            <WorkflowTab agentId={agentId as string} triggerType={watch('trigger_type')} />
          )}

          {tab === 'test' && isEdit && (
            <>
              {(isDirty || (headVersion != null && headVersion !== liveVersion)) && (
                <Alert tone="warning" className="mb-3">
                  The test chat talks to the live agent{liveVersion != null ? ` (v${liveVersion})` : ''}.
                  {' '}Edits only reach it once they are saved and published.
                </Alert>
              )}
              <TestPanel agentId={agentId as string} agentName={agentName} />
            </>
          )}

          {tab === 'prompt' && <PromptTab modules={previewModules} />}

          {tab === 'versions' && isEdit && (
            <VersionsTab agentId={agentId as string} headVersion={headVersion} hasUnsavedEdits={isDirty} />
          )}
        </PageShell>

        {/* ── Modals ── */}
        {modal?.kind === 'saveVersion' && (
          <SaveVersionModal
            nextVersion={nextVersionN}
            saving={updating}
            error={saveError}
            onClose={() => setModal(null)}
            onSave={confirmSave}
          />
        )}
      </form>
      </LayoutDraftsContext.Provider>
      </PromptChecksContext.Provider>
    </FormProvider>
  );
};

// ── AdvancedTab (inline — only 2 static cards) ─────────────────────────────────

import { useFormContext } from 'react-hook-form';
import { DescriptorField, SectionCard } from '@/components/Agent/agentShared';
import { ParameterDescriptor } from '@/redux/internal-services/agentsApi';

const AdvancedTab: React.FC<{ paramGroups: Record<string, ParameterDescriptor[]> }> = ({ paramGroups }) => {
  const { watch, setValue } = useFormContext<AgentFormValues>();

  const voiceParams = watch('voice') || {};

  const setVoiceField = (key: string, value: unknown) =>
    setValue('voice', { ...voiceParams, [key]: value }, { shouldDirty: true });

  const voiceDescriptors = paramGroups['voice'] || [];

  return (
    <>
      <SectionCard title="Voice / STT / TTS">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {voiceDescriptors.map((d) => (
            <DescriptorField
              key={d.key}
              d={d}
              value={voiceParams[d.key] ?? d.default}
              onChange={(v) => setVoiceField(d.key, v)}
            />
          ))}
          {voiceDescriptors.length === 0 && (
            <EmptyState variant="inline" title="No voice parameters defined." />
          )}
        </div>
      </SectionCard>
    </>
  );
};

export default AgentConfigPage;
